import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import {
  AddRounded as AddIcon,
  EditRounded as EditIcon
} from '@material-ui/icons';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Line } from 'components';
import {
  AuthContext,
  baseUrl,
  getApplicationDisplayDescription,
  getApplicationDisplayName,
  getApplicationHost,
  getApplicationIconUrl,
  getApplicationInitials,
  useSpot
} from 'framework';
import { Org, OrgApplication, User } from 'domain-types';

const ApplicationCard = styled(Box)`
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: ${p => p.theme.spacing(1)}px;
  margin-bottom: ${p => p.theme.spacing(2)}px;
  padding: ${p => p.theme.spacing(2)}px;
`;

const HeaderActions = styled(Box)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(2)}px;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing(2)}px;
`;

const SummaryRow = styled(Box)`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing(2)}px;
  justify-content: space-between;
`;

const SummaryMain = styled(Box)`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing(2)}px;
  min-width: 0;
`;

const SummaryText = styled(Box)`
  min-width: 0;
`;

const SummaryMeta = styled(Box)`
  color: ${p => p.theme.palette.text.secondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1, 2)};
  margin-top: ${p => p.theme.spacing(0.5)}px;
`;

const SummaryDescription = styled(Typography)`
  color: ${p => p.theme.palette.text.secondary};
  margin-top: ${p => p.theme.spacing(0.5)}px;
`;

const SummaryActions = styled(Box)`
  display: flex;
  flex-shrink: 0;
  gap: ${p => p.theme.spacing(1)}px;
`;

const EditorPanel = styled(Box)`
  border-top: 1px solid ${p => p.theme.palette.divider};
  margin-top: ${p => p.theme.spacing(2)}px;
  padding-top: ${p => p.theme.spacing(2)}px;
`;

const ApplicationFields = styled(Box)`
  display: grid;
  gap: ${p => p.theme.spacing(2)}px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const FullWidthField = styled(Box)`
  grid-column: 1 / -1;
`;

const ApplicationActions = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)}px;
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const InheritedAccessNotice = styled(Box)`
  align-items: center;
  background: ${p => p.theme.palette.grey[100]};
  border-radius: ${p => p.theme.spacing(1)}px;
  display: flex;
  grid-column: 1 / -1;
  min-height: 56px;
  padding: ${p => p.theme.spacing(1.5, 2)};
`;

const InheritedAccessText = styled(Typography)`
  color: ${p => p.theme.palette.text.secondary};
`;

const HeaderRow = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(2)}px;
`;

const AppIconFallback = styled(Box)`
  align-items: center;
  background: ${p => p.theme.palette.grey[200]};
  border-radius: 12px;
  display: flex;
  font-weight: 600;
  height: 44px;
  justify-content: center;
  text-transform: uppercase;
  width: 44px;
`;

const AppIconImage = styled.img`
  border-radius: 12px;
  height: 44px;
  object-fit: contain;
  width: 44px;
`;

const getErrorMessage = (t: (key: string) => string, error: unknown) => {
  if (Array.isArray(error) && error.length) {
    const firstError = error[0] as {
      body?: { error?: string };
      error?: string;
      message?: string;
    };

    return (
      firstError?.body?.error ||
      firstError?.error ||
      firstError?.message ||
      t('applicationSaveFailed')
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return t('applicationSaveFailed');
};

interface EditableOrgApplication extends OrgApplication {
  selectedUserIds: string[];
  prefetchedUrl?: string;
  prefetchedName?: string;
  prefetchedDescription?: string;
  prefetchedIconUrl?: string;
}

const dedupeIds = (values: string[]) =>
  values.filter(
    (value, index) => value.trim().length > 0 && values.indexOf(value) === index
  );

const normalizeApplicationUrl = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  if (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

const isValidApplicationUrl = (value: string) => {
  try {
    const url = new URL(normalizeApplicationUrl(value));
    return ['http:', 'https:'].includes(url.protocol);
  } catch (_error) {
    return false;
  }
};

const shouldReplacePrefilledText = (
  currentValue?: string,
  previousPrefilledValue?: string
) => {
  const normalizedCurrentValue = currentValue?.trim() || '';

  return (
    normalizedCurrentValue.length === 0 ||
    normalizedCurrentValue === (previousPrefilledValue || '')
  );
};

const shouldReplacePrefilledIcon = (
  currentValue?: string,
  previousPrefilledValue?: string
) => !currentValue || currentValue === previousPrefilledValue;

const toPersistedApplication = (
  application: EditableOrgApplication
): OrgApplication => ({
  id: application.id,
  name: application.name,
  url: normalizeApplicationUrl(application.url),
  description: application.description,
  iconUrl: application.iconUrl,
  visibility: application.visibility
});

function ApplicationIconPreview({
  application
}: {
  application: OrgApplication;
}) {
  const [imgError, setImgError] = useState(false);
  const iconUrl = getApplicationIconUrl(application);

  if (!iconUrl || imgError) {
    return (
      <AppIconFallback>{getApplicationInitials(application)}</AppIconFallback>
    );
  }

  return (
    <AppIconImage alt="" src={iconUrl} onError={() => setImgError(true)} />
  );
}

const toEditableApplications = (
  applications: OrgApplication[] = [],
  users: Record<string, User> = {}
): EditableOrgApplication[] =>
  applications.map(application => ({
    ...application,
    selectedUserIds: Object.values(users)
      .filter(user => (user.applicationIds ?? []).includes(application.id))
      .map(user => user.id)
  }));

export function OrgAdminApplicationsTab({
  organization,
  org,
  readOnly,
  updateOrgFields
}: {
  organization: string;
  org?: Org;
  readOnly: boolean;
  updateOrgFields: (fields: Partial<Org>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { getToken } = useContext(AuthContext);
  const { data, command, query } = useSpot();
  const [applications, setApplications] = useState<EditableOrgApplication[]>(
    []
  );
  const [error, setError] = useState('');
  const [editingApplicationId, setEditingApplicationId] = useState<
    string | null
  >(null);
  const [prefillingId, setPrefillingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const orgApplications = org?.applications;
  const searchUsers = data?.searchUsers;

  useEffect(() => {
    query(`org/${organization}/search`, {}, ['searchUsers']).catch(
      () => undefined
    );
  }, [organization, query]);

  useEffect(() => {
    setApplications(
      toEditableApplications(orgApplications ?? [], searchUsers ?? {})
    );
  }, [orgApplications, searchUsers]);

  const users = useMemo(
    () =>
      Object.values(searchUsers ?? {}).sort((left, right) =>
        `${left.firstName} ${left.lastName ?? ''}`.localeCompare(
          `${right.firstName} ${right.lastName ?? ''}`
        )
      ),
    [searchUsers]
  );

  const editingApplication = useMemo(
    () =>
      applications.find(application => application.id === editingApplicationId),
    [applications, editingApplicationId]
  );

  useEffect(() => {
    if (!editingApplicationId || !editingApplication) {
      return undefined;
    }

    const nextUrl = editingApplication.url.trim();
    const normalizedNextUrl = normalizeApplicationUrl(nextUrl);

    if (
      !isValidApplicationUrl(nextUrl) ||
      editingApplication.prefetchedUrl === normalizedNextUrl
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      setPrefillingId(editingApplicationId);

      try {
        const metadataResponse = await fetch(
          `${baseUrl}/org/${organization}/application-metadata`,
          {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
              authorization: getToken().getJwtToken()
            },
            body: JSON.stringify({ url: normalizedNextUrl })
          }
        );

        if (!metadataResponse.ok) {
          throw new Error('Unable to fetch application metadata');
        }

        const metadata = (await metadataResponse.json()) as Pick<
          OrgApplication,
          'name' | 'description' | 'iconUrl'
        >;

        setApplications(currentApplications =>
          currentApplications.map(application => {
            if (
              application.id !== editingApplicationId ||
              application.url.trim() !== nextUrl
            ) {
              return application;
            }

            const nextName = shouldReplacePrefilledText(
              application.name,
              application.prefetchedName
            )
              ? metadata.name || application.name
              : application.name;
            const nextDescription = shouldReplacePrefilledText(
              application.description,
              application.prefetchedDescription
            )
              ? metadata.description || ''
              : application.description;
            const nextIconUrl = shouldReplacePrefilledIcon(
              application.iconUrl,
              application.prefetchedIconUrl
            )
              ? metadata.iconUrl
              : application.iconUrl;

            return {
              ...application,
              name: nextName,
              description: nextDescription,
              iconUrl: nextIconUrl,
              prefetchedUrl: normalizedNextUrl,
              prefetchedName: metadata.name || '',
              prefetchedDescription: metadata.description || '',
              prefetchedIconUrl: metadata.iconUrl || ''
            };
          })
        );
      } catch (_error) {
        setApplications(currentApplications =>
          currentApplications.map(application =>
            application.id === editingApplicationId &&
            application.url.trim() === nextUrl
              ? { ...application, prefetchedUrl: normalizedNextUrl }
              : application
          )
        );
      } finally {
        setPrefillingId(currentId =>
          currentId === editingApplicationId ? null : currentId
        );
      }
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [editingApplication, editingApplicationId, getToken, organization]);

  const revertApplication = (applicationId: string) => {
    const sourceApplication = toEditableApplications(
      orgApplications ?? [],
      searchUsers ?? {}
    ).find(application => application.id === applicationId);

    setApplications(currentApplications => {
      if (!sourceApplication) {
        return currentApplications.filter(
          application => application.id !== applicationId
        );
      }

      return currentApplications.map(application =>
        application.id === applicationId ? sourceApplication : application
      );
    });
  };

  const updateDraftApplication = (
    applicationId: string,
    updates: Partial<EditableOrgApplication>
  ) => {
    setApplications(currentApplications =>
      currentApplications.map(application =>
        application.id === applicationId
          ? { ...application, ...updates }
          : application
      )
    );
  };

  const syncSelectedUsers = async (
    applicationId: string,
    selectedUserIds: string[]
  ) => {
    const currentUsers = Object.values(data?.searchUsers ?? {});
    const changedUsers = currentUsers.filter(user => {
      const currentApplicationIds = user.applicationIds ?? [];
      const hasApplication = currentApplicationIds.includes(applicationId);
      const shouldHaveApplication = selectedUserIds.includes(user.id);
      return hasApplication !== shouldHaveApplication;
    });

    await Promise.all(
      changedUsers.map(user => {
        const currentApplicationIds = user.applicationIds ?? [];
        const nextApplicationIds = selectedUserIds.includes(user.id)
          ? [...currentApplicationIds, applicationId]
          : currentApplicationIds.filter(
              currentApplicationId => currentApplicationId !== applicationId
            );

        return command(`org/${organization}/user/${user.id}`, {
          applicationIds: dedupeIds(nextApplicationIds)
        });
      })
    );
  };

  const saveApplication = async (applicationId: string) => {
    const draftApplication = applications.find(
      application => application.id === applicationId
    );

    if (!draftApplication) {
      return;
    }

    setError('');
    setSavingId(applicationId);

    try {
      await updateOrgFields({
        applications: applications.map(toPersistedApplication)
      });

      if (draftApplication.visibility === 'selected_users') {
        await syncSelectedUsers(
          applicationId,
          draftApplication.selectedUserIds
        );
      }

      await query(`org/${organization}/search`, {}, ['searchUsers']);
      setEditingApplicationId(null);
    } catch (saveError) {
      setError(getErrorMessage(t, saveError));
    } finally {
      setSavingId(null);
    }
  };

  const deleteApplication = async (applicationId: string) => {
    const currentOrgHasApplication = (org?.applications ?? []).some(
      application => application.id === applicationId
    );

    setApplications(currentApplications =>
      currentApplications.filter(
        application => application.id !== applicationId
      )
    );

    if (!currentOrgHasApplication) {
      return;
    }

    setError('');
    setSavingId(applicationId);

    try {
      await updateOrgFields({
        applications: applications
          .filter(application => application.id !== applicationId)
          .map(toPersistedApplication)
      });

      await syncSelectedUsers(applicationId, []);
      await query(`org/${organization}/search`, {}, ['searchUsers']);
      setEditingApplicationId(currentId =>
        currentId === applicationId ? null : currentId
      );
    } catch (deleteError) {
      setError(getErrorMessage(t, deleteError));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <Typography variant="body2" color="textSecondary">
        {t('applicationsDescription')}
      </Typography>
      <Line />
      {error ? <Alert severity="error">{error}</Alert> : null}
      <HeaderActions>
        <Typography variant="body2" color="textSecondary">
          {applications.length
            ? t('applicationListHint')
            : t('applicationNoOrgApps')}
        </Typography>
        <Button
          color="primary"
          disabled={readOnly}
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => {
            const nextApplicationId = `app-${Date.now()}`;
            setApplications(currentApplications => [
              ...currentApplications,
              {
                id: nextApplicationId,
                name: '',
                url: '',
                description: '',
                visibility: 'selected_users',
                selectedUserIds: []
              }
            ]);
            setEditingApplicationId(nextApplicationId);
          }}
        >
          {t('addApplication')}
        </Button>
      </HeaderActions>
      <Box mt={2}>
        {applications.length ? null : (
          <Alert severity="info">{t('applicationNoOrgApps')}</Alert>
        )}
        {applications.map(application => {
          const selectedUsers = users.filter(user =>
            application.selectedUserIds.includes(user.id)
          );
          const isEditing = editingApplicationId === application.id;
          const accessSummary =
            application.visibility === 'all_users'
              ? t('applicationAccessAllUsers')
              : t('applicationAssignedUsersCount', {
                  count: selectedUsers.length
                });

          return (
            <ApplicationCard key={application.id}>
              <SummaryRow>
                <SummaryMain>
                  <ApplicationIconPreview application={application} />
                  <SummaryText>
                    <HeaderRow>
                      <Typography variant="subtitle1">
                        {getApplicationDisplayName(application) ||
                          t('applicationName')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {application.id}
                      </Typography>
                    </HeaderRow>
                    {getApplicationDisplayDescription(application) ? (
                      <SummaryDescription variant="body2">
                        {getApplicationDisplayDescription(application)}
                      </SummaryDescription>
                    ) : null}
                    <SummaryMeta>
                      <Typography variant="caption">
                        {getApplicationHost(application.url)}
                      </Typography>
                      <Typography variant="caption">{accessSummary}</Typography>
                    </SummaryMeta>
                  </SummaryText>
                </SummaryMain>
                <SummaryActions>
                  <Button
                    color="primary"
                    disabled={readOnly || savingId === application.id}
                    size="small"
                    startIcon={<EditIcon />}
                    variant="outlined"
                    onClick={() =>
                      setEditingApplicationId(currentId =>
                        currentId === application.id ? null : application.id
                      )
                    }
                  >
                    {isEditing ? t('cancel') : t('editApplication')}
                  </Button>
                </SummaryActions>
              </SummaryRow>
              {isEditing ? (
                <EditorPanel>
                  <ApplicationFields>
                    <FullWidthField>
                      <TextField
                        autoFocus
                        disabled={readOnly}
                        fullWidth
                        label={t('applicationUrl')}
                        placeholder="https://app.example.com"
                        value={application.url}
                        variant="outlined"
                        helperText={
                          prefillingId === application.id
                            ? t('applicationFetchingMetadata')
                            : undefined
                        }
                        onChange={event =>
                          updateDraftApplication(application.id, {
                            url: event.target.value
                          })
                        }
                      />
                    </FullWidthField>
                    <Box>
                      <TextField
                        disabled={readOnly}
                        fullWidth
                        label={t('applicationName')}
                        value={application.name}
                        variant="outlined"
                        onChange={event =>
                          updateDraftApplication(application.id, {
                            name: event.target.value
                          })
                        }
                      />
                    </Box>
                    <Box>
                      <TextField
                        disabled={readOnly}
                        fullWidth
                        label={t('applicationAccess')}
                        select
                        value={application.visibility}
                        variant="outlined"
                        onChange={event =>
                          updateDraftApplication(application.id, {
                            visibility: event.target
                              .value as OrgApplication['visibility']
                          })
                        }
                      >
                        <MenuItem value="all_users">
                          {t('applicationAccessAllUsers')}
                        </MenuItem>
                        <MenuItem value="selected_users">
                          {t('applicationAccessSelectedUsers')}
                        </MenuItem>
                      </TextField>
                    </Box>
                    <FullWidthField>
                      <TextField
                        disabled={readOnly}
                        fullWidth
                        label={t('applicationDescription')}
                        multiline
                        rows={2}
                        value={application.description ?? ''}
                        variant="outlined"
                        onChange={event =>
                          updateDraftApplication(application.id, {
                            description: event.target.value
                          })
                        }
                      />
                    </FullWidthField>
                    {application.visibility === 'selected_users' ? (
                      <FullWidthField>
                        <Autocomplete
                          disabled={readOnly}
                          multiple
                          options={users}
                          value={selectedUsers}
                          getOptionLabel={option =>
                            `${option.firstName} ${option.lastName ??
                              ''}`.trim() || option.email
                          }
                          onChange={(_event, nextUsers) =>
                            updateDraftApplication(application.id, {
                              selectedUserIds: nextUsers.map(user => user.id)
                            })
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              label={t('applicationAssignedUsers')}
                              variant="outlined"
                            />
                          )}
                        />
                      </FullWidthField>
                    ) : (
                      <InheritedAccessNotice>
                        <InheritedAccessText variant="body2">
                          {t('applicationInheritedAccessDescription')}
                        </InheritedAccessText>
                      </InheritedAccessNotice>
                    )}
                  </ApplicationFields>
                  <ApplicationActions>
                    <Button
                      color="secondary"
                      disabled={readOnly || savingId === application.id}
                      onClick={() => deleteApplication(application.id)}
                    >
                      {t('delete')}
                    </Button>
                    <Button
                      disabled={savingId === application.id}
                      onClick={() => {
                        revertApplication(application.id);
                        setEditingApplicationId(null);
                        setError('');
                      }}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      color="primary"
                      disabled={readOnly || savingId === application.id}
                      variant="contained"
                      onClick={() => saveApplication(application.id)}
                    >
                      {t('submit')}
                    </Button>
                  </ApplicationActions>
                </EditorPanel>
              ) : null}
            </ApplicationCard>
          );
        })}
      </Box>
    </>
  );
}
